// ============================================================================
// COMPANY DETAIL MODAL - Insights-Style Company Assessment View
// Detailed company risk breakdown and assessment actions
// ============================================================================

import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { formatCurrency } from '../../utils/enhancedFormatters';
import { getRiskBandStyle, getRiskBandLabel, legacyCrsBandToKey } from '../../utils/bands';
import { measuredRiskScore } from './measuredRiskScore';

// The Big 6, in display order, with the backend's keys.
const BIG6_LABELS = [
  ['governance', 'Governance'],
  ['technicalDebt', 'Technical Debt'],
  ['culture', 'Culture'],
  ['thirdParty', 'Third Party'],
  ['budget', 'Budget & Resources'],
  ['people', 'People'],
];

// The posture badge for a client with no score yet: neutral, never a risk band.
const NOT_MEASURED_STYLE = {
  bg: 'rgba(136,146,176,0.12)', text: '#a8b2d1', border: 'rgba(136,146,176,0.35)',
  color: '#8892b0', stroke: '#8892b0',
  icon: '—', label: 'Not measured', description: 'Available once every persona has finished',
};
import apiConfig from '../../config/apiConfig';

// PRISM Integration section — shown inside CompanyDetailModal.
// Reads prismPosture from shared_integration projections passed in by parent.
// Gracefully handles three states: not-linked, linked-but-no-data, linked-with-data.
const ATO_STEP_LABELS_DM = {
  1: 'Categorize', 2: 'Select', 3: 'Implement',
  4: 'Assess',    5: 'Authorize', 6: 'Monitor'
};

const PrismIntegrationSection = ({ posture, linked }) => {
  if (!linked && !posture) {
    return (
      <div className="p-6 border-b border-[rgba(255,255,255,0.12)]">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-[#e6f1ff]">PRISM Integration</h3>
            <p className="text-sm text-[#a8b2d1] mt-1">
              This client has no active PRISM project linked. SPRS, ATO Journey, and drift
              signals will appear here once Foundations is bridged to PRISM.
            </p>
          </div>
          <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-500/20 text-slate-300 border border-slate-400/30">
            Not linked
          </span>
        </div>
      </div>
    );
  }

  const hasSprs = posture?.sprs_score != null;
  const hasAto = posture?.ato_step != null;
  const hasDrift = (posture?.drift_open || 0) > 0;
  const nothing = !hasSprs && !hasAto && !hasDrift;

  const sprsColor =
    !hasSprs ? 'text-[#64748b]' :
    posture.sprs_score >= 80 ? 'text-emerald-300' :
    posture.sprs_score >= 50 ? 'text-amber-300' :
    posture.sprs_score >= 0  ? 'text-orange-300' :
                               'text-red-300';
  const severityColor = (sev) => ({
    critical: 'bg-red-500/20 text-red-200 border-red-400/40',
    high:     'bg-orange-500/20 text-orange-200 border-orange-400/40',
    medium:   'bg-amber-500/20 text-amber-200 border-amber-400/40',
    low:      'bg-slate-500/20 text-slate-200 border-slate-400/40'
  })[sev] || 'bg-slate-500/10 text-slate-300';

  return (
    <div className="p-6 border-b border-[rgba(255,255,255,0.12)]">
      <div className="flex items-start justify-between mb-4">
        <h3 className="text-lg font-semibold text-[#e6f1ff]">PRISM Integration</h3>
        <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 border border-emerald-400/40">
          Linked
        </span>
      </div>

      {nothing ? (
        <p className="text-sm text-[#a8b2d1]">
          PRISM project is linked, but no SPRS score, ATO Journey, or drift data has been
          generated yet. These will populate as work happens on the PRISM side.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* SPRS */}
          <div className="rounded-lg border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.03)] p-4">
            <div className="text-[10px] uppercase tracking-wide text-[#8892b0]">SPRS · DFARS 7012</div>
            {hasSprs ? (
              <>
                <div className={`text-3xl font-bold mt-1 ${sprsColor}`}>{posture.sprs_score}</div>
                <div className="text-[11px] text-[#a8b2d1] mt-1">
                  {posture.sprs_submitted
                    ? <span className="text-emerald-300">Submission on file</span>
                    : <span className="text-[#8892b0]">Not submitted</span>}
                </div>
              </>
            ) : (
              <div className="text-sm text-[#8892b0] italic mt-2">No score yet</div>
            )}
          </div>

          {/* ATO Journey */}
          <div className="rounded-lg border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.03)] p-4">
            <div className="text-[10px] uppercase tracking-wide text-[#8892b0]">ATO Journey</div>
            {hasAto ? (
              <>
                <div className="text-xl font-bold text-[#e6f1ff] mt-1">
                  Step {posture.ato_step}
                  <span className="ml-1 text-sm text-[#a8b2d1] font-normal">· {ATO_STEP_LABELS_DM[posture.ato_step]}</span>
                </div>
                {posture.ato_percent != null && (
                  <div className="mt-2">
                    <div className="h-1.5 rounded-full bg-[rgba(255,255,255,0.06)] overflow-hidden">
                      <div className="h-full bg-cyan-400" style={{ width: `${Math.min(100, posture.ato_percent)}%` }} />
                    </div>
                    <div className="text-[11px] text-[#a8b2d1] mt-1">{Math.round(posture.ato_percent)}% complete</div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-sm text-[#8892b0] italic mt-2">No active journey</div>
            )}
          </div>

          {/* Drift */}
          <div className="rounded-lg border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.03)] p-4">
            <div className="text-[10px] uppercase tracking-wide text-[#8892b0]">Open Drift</div>
            {hasDrift ? (
              <>
                <div className="flex items-baseline gap-2 mt-1">
                  <div className="text-3xl font-bold text-[#e6f1ff]">{posture.drift_open}</div>
                  {posture.max_drift_severity && (
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${severityColor(posture.max_drift_severity)}`}>
                      {posture.max_drift_severity}
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-[#a8b2d1] mt-1">Unacknowledged drift events</div>
              </>
            ) : (
              <div className="text-sm text-[#8892b0] italic mt-2">No open drift</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const CompanyDetailModal = ({ company, isOpen, onClose, onActionClick, prismPosture = null, prismLinked = false }) => {
  const [crsData, setCrsData] = useState(null);
  const [crsLoading, setCrsLoading] = useState(false);

  // Fetch CRS data when modal opens
  useEffect(() => {
    if (isOpen && company) {
      fetchCRSData();
    }
  }, [isOpen, company]);

  const fetchCRSData = async () => {
    const companyId = company.company_id || company.id;
    if (!companyId) return;

    setCrsLoading(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('foundations-token');
      const response = await fetch(`${apiConfig.getApiBaseUrl()}/portfolio/crs/${companyId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setCrsData(result.data);
        }
      }
    } catch (error) {
      console.error('Error fetching CRS data:', error);
    } finally {
      setCrsLoading(false);
    }
  };

  if (!company) return null;

  // Extract company data. riskScore (the CRS) is null until the client has one.
  const riskScore = measuredRiskScore(company);
  const scored = riskScore !== null;
  const industryType = company.industry || company.industry_vertical || 'Unknown';
  const portfolioName = company.portfolio_name || 'N/A';
  const companyName = company.company_name || company.name || 'Unknown Company';

  // Revenue at risk, recovery days and the Big 6 come from the backend: the same business-
  // intelligence computation as the client's own Compass (Pat, 2026-10-19). This modal used to
  // estimate them from the CRS with its own tier table, and drew six bars as the score x fixed
  // factors (one, "Cyber Security", is not a Big 6 category). Absent is "Not measured".
  const revenueAtRisk = company.revenue_at_risk ?? null;
  const recoveryDays = company.recovery_days ?? null;
  const hasFinancials = revenueAtRisk !== null && recoveryDays !== null;
  const riskCategories = company.big_six
    ? BIG6_LABELS.map(([key, name]) => ({ name, score: Math.round(company.big_six[key]) }))
    : null;

  const riskBandStyle = scored ? getRiskBandStyle(riskScore) : NOT_MEASURED_STYLE;

  // Assessment actions
  const assessmentActions = [
    {
      title: 'View Full Assessment',
      icon: '📋',
      color: 'bg-blue-600 hover:bg-blue-700',
      action: 'view'
    },
    {
      title: 'Start New Assessment',
      icon: '🔄',
      color: 'bg-green-600 hover:bg-green-700',
      action: 'new'
    },
    {
      title: 'View Trends',
      icon: '📈',
      color: 'bg-purple-600 hover:bg-purple-700',
      action: 'trends'
    },
    {
      title: 'Export Report',
      icon: '📤',
      color: 'bg-orange-600 hover:bg-orange-700',
      action: 'export'
    }
  ];

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-50 z-50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 ra-glass rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.3)] max-w-4xl w-full max-h-[90vh] overflow-y-auto p-0">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-[rgba(255,255,255,0.12)]">
          <div>
            <Dialog.Title className="text-2xl font-bold text-[#e6f1ff]">{companyName}</Dialog.Title>
            <div className="flex items-center mt-1 space-x-4 text-sm text-[#a8b2d1]">
              <span>Portfolio: {portfolioName}</span>
              <span>Industry: {industryType}</span>
            </div>
          </div>
          <Dialog.Close asChild>
            <button
              aria-label="Close"
              className="p-2 text-[#5a6a8a] hover:text-[#a8b2d1] hover:bg-[rgba(255,255,255,0.12)] rounded-full transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </Dialog.Close>
        </div>

        {/* Risk Assessment Overview */}
        <div className="p-6 bg-blue-50 border-b border-[rgba(255,255,255,0.12)]">
          <div className="flex items-center mb-4">
            <div className="w-3 h-3 rounded-full bg-blue-600 mr-3"></div>
            <h3 className="text-lg font-semibold text-[#e6f1ff]">Risk Assessment</h3>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {/* Overall Risk Posture — banded, no raw numeric */}
            <div className="text-center">
              <div className="text-sm text-[#a8b2d1] mb-2">Overall Risk Posture</div>
              <div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-base font-semibold"
                style={{ background: riskBandStyle.bg, color: riskBandStyle.text, border: `1px solid ${riskBandStyle.border}` }}
              >
                <span aria-hidden="true" className="text-lg">{riskBandStyle.icon}</span>
                <span>{riskBandStyle.label}</span>
              </div>
              <div className="text-xs text-[#8892b0] mt-2">{riskBandStyle.description}</div>
            </div>

            {/* Revenue at Risk */}
            <div className="text-center">
              <div className="text-3xl font-bold text-[#e6f1ff] mb-1">
                {hasFinancials ? formatCurrency(revenueAtRisk) : <span className="text-base text-[#8892b0]">Not measured</span>}
              </div>
              <div className="text-sm text-[#a8b2d1] mb-2">Revenue at Risk</div>
              <div className="text-xs text-[#8892b0]">Annual potential loss</div>
              {hasFinancials && company.revenue_at_risk_calculation && (
                <button
                  className="text-blue-600 hover:text-blue-700 text-xs underline"
                  onClick={() => onActionClick && onActionClick('calculation', company)}
                >
                  View Calculation
                </button>
              )}
            </div>

            {/* Recovery Days */}
            <div className="text-center">
              <div className="text-3xl font-bold text-[#e6f1ff] mb-1">
                {hasFinancials ? recoveryDays : <span className="text-base text-[#8892b0]">Not measured</span>}
              </div>
              <div className="text-sm text-[#a8b2d1] mb-2">Recovery Days</div>
              <div className="text-xs text-[#8892b0]">Expected downtime</div>
            </div>

            {/* Days Since Assessment */}
            <div className="text-center">
              <div className="text-3xl font-bold text-[#e6f1ff] mb-1">--</div>
              <div className="text-sm text-[#a8b2d1] mb-2">Days Since Assessment</div>
              <div className="text-xs text-[#8892b0]">Assessment completed</div>
            </div>
          </div>
        </div>

        {/* Cyber Risk Signal Section */}
        <div className="p-6 border-b border-[rgba(255,255,255,0.12)] ra-glass">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-indigo-600 mr-3"></div>
              <h3 className="text-lg font-semibold text-[#e6f1ff]">Cyber Risk Signal™</h3>
              {crsData?.isEstimated && (
                <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-[#ccd6f6] text-xs rounded-full">
                  Estimated
                </span>
              )}
            </div>
          </div>

          {crsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full"></div>
              <span className="ml-3 text-[#a8b2d1]">Calculating CRS...</span>
            </div>
          ) : crsData ? (
            <div className="space-y-4">
              {/* CRS Posture — banded, no raw numeric */}
              {(() => {
                const crsBandKey = crsData.band ? legacyCrsBandToKey(crsData.band) : 'moderate';
                // Re-pin to a representative score so we get the canonical band-style object.
                const repScore = crsBandKey === 'critical' ? 100 : crsBandKey === 'high' ? 70 : crsBandKey === 'moderate' ? 45 : 15;
                const crsStyle = getRiskBandStyle(repScore);
                return (
                  <div className="flex items-center gap-6">
                    <div
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-lg text-lg font-bold"
                      style={{ background: crsStyle.bg, color: crsStyle.text, border: `1px solid ${crsStyle.border}` }}
                    >
                      <span aria-hidden="true" className="text-2xl">{crsStyle.icon}</span>
                      <span>{crsStyle.label}</span>
                    </div>
                    <div className="flex-1">
                      <div className="text-sm text-[#a8b2d1]">
                        {crsData.momentum === 'improving' ? '📈 Improving' :
                         crsData.momentum === 'deteriorating' ? '📉 Deteriorating' :
                         '➡️ Stable'} • {crsData.peerPosition?.label || 'N/A'} vs peers
                      </div>
                      <div className="text-xs text-[#8892b0] mt-1">{crsStyle.description}</div>
                    </div>
                  </div>
                );
              })()}

              {/* CRS Components — band-only, no raw numerics */}
              {crsData.components && (
                <div className="grid grid-cols-5 gap-2 mt-4">
                  {[
                    { key: 'externalPressure', label: 'External', icon: '🌐' },
                    { key: 'internalFragility', label: 'Internal', icon: '🏢' },
                    { key: 'governanceRisk', label: 'Governance', icon: '📋' },
                    { key: 'trajectory', label: 'Trajectory', icon: '📊' },
                    { key: 'organizationalStress', label: 'Stress', icon: '👥' }
                  ].map(({ key, label, icon }) => {
                    const v = crsData.components[key];
                    const cs = v != null ? getRiskBandStyle(v) : null;
                    return (
                      <div key={key} className="ra-glass rounded-lg p-2 text-center shadow-[0_1px_3px_rgba(0,0,0,0.3)]">
                        <div className="text-lg">{icon}</div>
                        {cs ? (
                          <div
                            className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
                            style={{ background: cs.bg, color: cs.text, border: `1px solid ${cs.border}` }}
                          >
                            <span aria-hidden="true">{cs.icon}</span>
                            <span>{cs.label}</span>
                          </div>
                        ) : (
                          <div className="mt-1 text-xs text-[#8892b0]">N/A</div>
                        )}
                        <div className="text-xs text-[#8892b0] mt-1">{label}</div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Decision Triggers */}
              {crsData.triggers && crsData.triggers.length > 0 && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="text-sm font-semibold text-[#ccd6f6] mb-1">⚠️ Decision Triggers</div>
                  <ul className="text-sm text-red-700 space-y-1">
                    {crsData.triggers.map((trigger, idx) => (
                      <li key={idx}>• {trigger}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-[#8892b0]">
              <div className="text-2xl mb-2">📊</div>
              <div>CRS data unavailable</div>
            </div>
          )}
        </div>

        {/* Risk Categories Analysis */}
        <div className="p-6 border-b border-[rgba(255,255,255,0.12)]">
          <h3 className="text-lg font-semibold text-[#e6f1ff] mb-4">Risk Categories Analysis</h3>
          
          {!riskCategories && (
            <div className="text-sm text-[#8892b0] italic">
              Not measured — available once every persona has finished the assessment.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {riskCategories && riskCategories.map((category, index) => {
              const cappedScore = Math.min(category.score, 100);
              const catStyle = getRiskBandStyle(cappedScore);
              return (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-[#ccd6f6]">{category.name}</span>
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
                        style={{ background: catStyle.bg, color: catStyle.text, border: `1px solid ${catStyle.border}` }}
                      >
                        <span aria-hidden="true">{catStyle.icon}</span>
                        <span>{catStyle.label}</span>
                      </span>
                    </div>
                    <div className="w-full bg-[#1e3148] rounded-full h-2">
                      <div
                        className="h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${cappedScore}%`,
                          backgroundColor: catStyle.stroke
                        }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* PRISM Integration — FA-11 detail section */}
        <PrismIntegrationSection posture={prismPosture} linked={prismLinked} />

        {/* Company Information */}
        <div className="p-6 border-b border-[rgba(255,255,255,0.12)]">
          <h3 className="text-lg font-semibold text-[#e6f1ff] mb-4">Company Information</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="text-sm font-medium text-[#ccd6f6] mb-1">Company Name</div>
              <div className="text-[#e6f1ff]">{companyName}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-[#ccd6f6] mb-1">Industry</div>
              <div className="text-[#e6f1ff]">{industryType}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-[#ccd6f6] mb-1">Company ID</div>
              <div className="text-[#e6f1ff]">{company.company_id || company.id || 'N/A'}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-[#ccd6f6] mb-1">Portfolio</div>
              <div className="text-[#e6f1ff]">{portfolioName}</div>
            </div>
          </div>
        </div>

        {/* Assessment Actions */}
        <div className="p-6">
          <h3 className="text-lg font-semibold text-[#e6f1ff] mb-4">Assessment Actions</h3>
          
          <div className="flex flex-wrap gap-4">
            {assessmentActions.map((action, index) => (
              <button
                key={index}
                onClick={() => onActionClick && onActionClick(action.action, company)}
                className={`${action.color} text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2`}
              >
                <span>{action.icon}</span>
                <span>{action.title}</span>
              </button>
            ))}
          </div>

          <div className="mt-4 p-3 bg-[rgba(255,255,255,0.03)] rounded-lg">
            <p className="text-sm text-[#a8b2d1]">
              Click outside to close or use the action buttons above
            </p>
          </div>
        </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default CompanyDetailModal;